<template>
  <navbar>
    <div class="navbar-wrapper">
      <div class="navbar-header-left">
        <div
          v-if="!isTrainingOperator"
          class="navbar-minimize"
        >
          <button
            id="minimizeSidebar"
            class="btn btn-icon btn-round"
            @click="minimizeSidebar"
          >
            <i class="nc-icon nc-minimal-right text-center visible-on-sidebar-mini" />
            <i class="nc-icon nc-minimal-left text-center visible-on-sidebar-regular" />
          </button>
        </div>
        <div class="navbar-toggle">
          <navbar-toggle-button
            :is-show-sidebar="status.isShowLeftSidebar"
            @click="toggleSidebar"
          />
        </div>
        <a class="navbar-brand">{{ pageName }}</a>
        <div
          v-if="isOperatorDashboard"
          class="favorite-icon"
          :class="isFavoritePage ? 'active' : ''"
          @click="onUpdateFavoriteTab"
        >
          <i class="fa fa-heart" />
        </div>

        <div
          v-if="isPartnerDashboard && partnerList && partnerList.length"
          class="partner-list"
        >
          <dropdown-list
            v-model="selectedPartnerId"
            placeholder="Choose Partner"
            value-key="id"
            :options="partnerList"
            @change="changePartner"
          />
        </div>
      </div>

      <div
        v-if="isManagementPage"
        class="navbar-header-center"
      >
        <el-tooltip
          v-for="(item, index) in navIcons"
          :key="index"
          :content="item.tooltip"
          effect="dark"
          placement="bottom"
        >
          <a
            :href="`#${item.pageId}`"
            class="nav-icon"
          >
            <i :class="item.icon" />
          </a>
        </el-tooltip>
      </div>

      <div class="navbar-header-center">
        <p-button
          v-if="isRegistrationPage"
          type="yellow"
          round
          @click="resetCurrentRegistrationData"
        >
          Clear all current registration data
        </p-button>

        <p-button
          v-if="estimationRide"
          type="purple"
          round
          @click="status.isShowRecallEstimateDialog = true"
        >
          Recall Estimate
        </p-button>
      </div>

      <div
        v-if="isAffiliateUser"
        class="navbar-header-center affiliate"
      >
        <span>Extension: {{ extension }}</span>
      </div>
      <!-- <div v-dompurify-html="scripts" v-if="isFamilyDashboard" /> -->

      <div class="navbar-header-right">
        <div
          v-if="isAvailableSwitchRole"
          class="redirection-button"
        >
          <button
            class="btn btn-outline-primary btn-round text-uppercase px-3 py-2"
            @click="onChangeUserRole"
          >
            <span class="is-desktop"> {{ changeRoleBtnText }} </span>
          </button>
        </div>

        <div
          v-if="isAvailableSwitchBoard"
          class="redirection-button"
        >
          <button
            class="btn btn-outline-primary btn-round text-uppercase px-3 py-2"
            @click="onChangeDashboard"
          >
            <span class="is-desktop">
              {{ isFamilyDashboard ? 'Go Back - Operator Dashboard' : 'Go To - Client Dashboard' }}
            </span>
            <span class="is-mobile">
              {{ isFamilyDashboard ? 'Operator' : 'Client' }}
            </span>
          </button>
        </div>

        <template v-if="isRightPanelVisible && !isTrainingOperator">
          <div class="navbar-toggler right-icon">
            <button
              class="btn btn-round btn-icon"
              @click="toggleRightSidebar"
            >
              <i
                class="nc-icon text-center visible-on-sidebar-regular"
                :class="status.isShowRightSidebar ? 'nc-minimal-left' : 'nc-minimal-right'"
              />
            </button>
          </div>
        </template>
      </div>
    </div>

    <RecallEstimationDialog
      :is-show-dialog="status.isShowRecallEstimateDialog"
      @cancel="status.isShowRecallEstimateDialog = false"
    />
  </navbar>
</template>

<script lang="ts">
import { ref, Ref, reactive, toRefs, computed, watch, getCurrentInstance } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import delay from 'yoctodelay';
import queryString from 'query-string';
import events from '@/events';

import { useCustomer } from '@/use/customer';
import { useProfile } from '@/use/profile';
import { useDashboard } from '@/use/app';
import { usePartner } from '@/use/partner';
import { useAffiliate } from '@/use/affiliate';
import { useSwalDialog } from '@/use/swalDialog';
import { estimationRide } from '@/use/registration';
import { Navbar, NavbarToggleButton } from '@/components/UIComponents';
import { viewVirtualDashboardKey } from '@/constants/app';

import RecallEstimationDialog from '@/components/Main/Dialogs/RecallEstimation.vue';

const navIcons = [
  { pageId: 'clear-user-fraud', icon: 'fa fa-user-md text-danger', tooltip: 'Clear User of Fraud Suspicion' },
  { pageId: 'transfer-ride', icon: 'fa fa-car text-success', tooltip: 'Transfer Ride to Another Account' },
  { pageId: 'operator-activities-summary', icon: 'fa fa-signal text-danger', tooltip: 'Operator Activities Summary' },
  { pageId: 'closed-accounts', icon: 'fa fa-user text-success', tooltip: 'Closed Accounts' },
  { pageId: 'assign-team-lead', icon: 'fa fa-users text-danger', tooltip: 'Assign TL to Operator' },
];

export default {
  name: 'TopNavbar',

  components: {
    Navbar,
    NavbarToggleButton,
    RecallEstimationDialog,
  },

  emits: ['toggle-left-sidebar', 'toggle-right-sidebar'],

  setup: (props: any, context: any) => {
    const { emit } = context;
    const route = useRoute();
    const router = useRouter();

    const instance: any = getCurrentInstance();

    const { currentUser, userRoles, addFavoritePage, removeFavoritePage, favoritePages } = useProfile();
    const { isCustomerUser, isFamilyUser, isAffiliateUser, isTrainingOperator, isManagementRole } = toRefs(userRoles);
    const {
      extension,
      // codeName,
    } = useAffiliate();

    const { isOperatorDashboard, isFamilyDashboard, isPartnerDashboard } = useDashboard();

    const { customerPhoneNumber, setCustomerPhoneNumber } = useCustomer();

    const { setPartnerInfo } = usePartner();

    const { showConfirmPopupDialog } = useSwalDialog();

    const partnerList: Ref<any> = ref([]);
    const selectedPartnerId = ref(0);
    const status = reactive({
      isShowLeftSidebar: false,
      isShowRightSidebar: false,
      isShowRecallEstimateDialog: false,
    });

    const dashboardLink = computed(() => {
      if (isFamilyDashboard.value) {
        return customerPhoneNumber.value
          ? `/operator/overview?${queryString.stringify({ phoneNumber: customerPhoneNumber.value })}`
          : '/operator';
      } else if (isOperatorDashboard.value) {
        return '/family/profile';
      }

      return '';
    });

    const isAvailableSwitchBoard = computed(() => {
      if (!currentUser.value?.id) {
        return false;
      }

      if (isCustomerUser.value || isFamilyUser.value || isAffiliateUser.value) {
        return false;
      }

      if (isPartnerDashboard.value) {
        return false;
      }

      return true;
    });

    const isAvailableSwitchRole = computed(() => {
      if (!currentUser.value?.id) {
        return false;
      }

      if (isFamilyDashboard.value || isPartnerDashboard.value) {
        return false;
      }

      if (isViewVirtualOperatorDashboard.value) {
        return true;
      }

      return isManagementRole.value;
    });

    const isViewVirtualOperatorDashboard = computed(() => !!(localStorage.getItem(viewVirtualDashboardKey) as string));
    const changeRoleBtnText = computed(() => (isViewVirtualOperatorDashboard.value ? 'Go To - Original View' : 'Go To - Operator View'));

    const isRightPanelVisible = computed(() => {
      if (!currentUser.value?.id) {
        return false;
      }

      if (isCustomerUser.value || isFamilyUser.value || isAffiliateUser.value) {
        return false;
      }

      return true;
    });

    const pageName = computed(() => formatRouteName(route.meta?.customPageName as string || route.name as string));

    const isManagementPage = computed(() => route.name === 'Management');
    const isRegistrationPage = computed(() => route.name === 'RegistrationPage');

    const isFavoritePage = computed(() => favoritePages.value.includes(route.path));

    const formatRouteName = (name: string) => {
      // 1. Protect "GoGo" with a placeholder *with spaces*
      const token = "__GOGO__";
      const protectedName = name.replace(/GoGo/g, ` ${token} `);

      // 2. Run normal PascalCase + acronym splitting
      let formatted = protectedName
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/\s+/g, " ") // collapse extra spaces
        .trim();

      // 3. Restore the protected GoGo
      formatted = formatted.replace(new RegExp(token, "g"), "GoGo");

      return formatted;
    }

    const getPartner = async () => {
      if (!currentUser.value?.partners) {
        return;
      }

      if (!isPartnerDashboard.value) {
        return;
      }

      partnerList.value = currentUser.value.partners.map((partner: any) => {
        return {
          id: partner.id,
          label: partner.name,
          codeName: partner.codeName,
          disabled: partner.isDisabled,
          plan: partner.plan,
          maxRides: partner.maxRides,
          timeZone: partner.timeZone,
          serviceCode: partner.serviceCode,
          isAutoConfirmSurge: partner.isAutoConfirmSurge,
          subsidyCondition: partner.subsidyCondition,
          isApprovedAddressPartner: partner.isApprovedAddressPartner,
          isSelfServeDashboard: partner.isSelfServeDashboard,
          rideAllowance: partner.rideAllowance ?? [],
          fieldConfig: partner.fieldConfig,
        };
      });

      // find first active partner
      const partner = partnerList.value.find((item: any) => !item.disabled);
      if (partner) {
        selectedPartnerId.value = partner.id;
        setPartnerInfo(partner);
      } else {
        setPartnerInfo(null);
      }
    };

    const changePartner = async () => {
      const partner = partnerList.value.find((item: any) => item.id === selectedPartnerId.value && !item.disabled);
      setPartnerInfo(partner);
      setCustomerPhoneNumber('');

      await delay(100);
      router.push('/partner/overview');
    };

    const removeNavOpenClass = () => {
      document.body.classList.remove('nav-open');
      document.body.classList.remove('left');
      document.body.classList.remove('right');
    };

    // toggle events for expand or collapse right & left side panels
    const toggleSidebar = () => {
      status.isShowLeftSidebar = !status.isShowLeftSidebar;
      instance.data.panelStore.displaySidebar(false);
      instance.data.sidebarStore.displaySidebar(!instance.data.sidebarStore.showSidebar);
      emit('toggle-left-sidebar', instance.data.sidebarStore.showSidebar);

      removeNavOpenClass();
      if (instance.data.sidebarStore.showSidebar) {
        document.body.classList.add('nav-open');
        document.body.classList.add('left');
      }
    };

    const toggleRightSidebar = () => {
      status.isShowRightSidebar = !status.isShowRightSidebar;
      instance.data.panelStore.isMinimized = !instance.data.panelStore.isMinimized;
      instance.data.sidebarStore.displaySidebar(false);
      instance.data.panelStore.displaySidebar(!instance.data.panelStore.showRightPanel);
      emit('toggle-right-sidebar', instance.data.panelStore.showRightPanel);

      removeNavOpenClass();
      if (instance.data.panelStore.showRightPanel) {
        document.body.classList.add('nav-open');
        document.body.classList.add('right');
      }
    };

    const minimizeSidebar = () => {
      instance.data.sidebarStore.toggleMinimize();
    };

    const onUpdateFavoriteTab = () => {
      const path = route.path;

      if (favoritePages.value.includes(path)) {
        removeFavoritePage(path);
      } else {
        addFavoritePage(path);
      }
    };

    const onChangeDashboard = async () => {
      if (isOperatorDashboard.value) {
        const isConfirmed = await showConfirmPopupDialog({
          text: "Please confirm you'd like to leave the operator dashboard and go to the client dashboard.",
          title: 'Are you sure to leave the operator dashboard?',
        });

        if (!isConfirmed) {
          return;
        }
      }

      router.push(dashboardLink.value);
    };

    const resetCurrentRegistrationData = () => {
      events.emit('reset-registration-data');
    };

    const onChangeUserRole = async () => {
      const text = isViewVirtualOperatorDashboard.value
        ? "Please confirm you'd like to view original dashboard"
        : "Please confirm you'd like to change dashboard view like operator";

      const isConfirmed = await showConfirmPopupDialog({
        text,
        title: 'Are you sure to change dashboard view?',
      });

      if (!isConfirmed) {
        return;
      }

      if (isViewVirtualOperatorDashboard.value) {
        localStorage.removeItem(viewVirtualDashboardKey);
      } else {
        localStorage.setItem(viewVirtualDashboardKey, 'true');
      }

      /*
        We need to update it here because the router and side menu are set up before we fetch the current user.
        If we don't, they'll continue using the old roles stored in local storage.
        The new roles won't be applied until after the user data is fetched.
      */
      const userRoles = isViewVirtualOperatorDashboard.value ? currentUser.value.originalRoles || [] : ['operator'];
      localStorage.setItem('roles', JSON.stringify(userRoles));

      location.reload();
    };

    watch(
      currentUser,
      async (newValue: any, oldValue: any) => {
        /*
        To avoid infinite loop, need to check oldValue.id
        When partner is chosen, we update currentUser.roles regarding with selected partner.
        And if currentUser is updated, watch statement will occur again.
        And oldValue.id is not exist, this is first time and need to get partner list
        Otherwise no need to get partner list of user.
      */

        if (oldValue?.id) {
          return;
        }

        await getPartner();
      },
      {
        immediate: true,
      },
    );

    watch(
      () => route.path,
      () => {
        if (status.isShowLeftSidebar) {
          toggleSidebar();
        }
      },
    );

    return {
      instance,
      navIcons,
      isOperatorDashboard,
      isFamilyDashboard,
      isPartnerDashboard,
      partnerList,
      selectedPartnerId,
      status,
      dashboardLink,
      isAvailableSwitchBoard,
      isRightPanelVisible,
      pageName,
      isManagementPage,
      isAffiliateUser,
      extension,
      isTrainingOperator,
      isFavoritePage,
      isRegistrationPage,
      isAvailableSwitchRole,
      changeRoleBtnText,
      estimationRide,

      changePartner,
      toggleSidebar,
      toggleRightSidebar,
      minimizeSidebar,
      onUpdateFavoriteTab,
      onChangeDashboard,
      resetCurrentRegistrationData,
      onChangeUserRole,
    };
  },
};
//   scripts: string = "<script text/javascript> \
//     (function (u, s, e, r, g) { \
//         u[r] = u[r] || []; \
//         u[r].push({ \
//           'ug.start': new Date().getTime(), event: 'embed.js', \
//         }); \
//         var f = s.getElementsByTagName(e)[0], \
//             j = s.createElement(e); \
//         j.async = true; \
//         j.src = 'https://static.userguiding.com/media/user-guiding-' \
//           + g + '-embedded.js'; \
//         f.parentNode.insertBefore(j, f); \
//     })(window, document, 'script', 'userGuidingLayer', '90438929ID');<\/script>"
</script>

<style lang="scss">
.main-panel.partner-dashboard {
  .navbar-header-left {
    width: 100%;
  }
}
.navbar {
  .navbar-wrapper {
    width: 100%;
    justify-content: space-between;

    .favorite-icon {
      font-size: 18px;
      color: #4957a6;
      cursor: pointer;

      &.active {
        color: #f00;
      }
    }
  }
  .navbar-header-left,
  .navbar-header-right {
    display: flex;
    align-items: center;
  }
  .navbar-header-center {
    height: 50px;
    display: flex;
    align-items: flex-end;
    flex: 1;
    flex-wrap: wrap;
    justify-content: center;
    .nav-icon {
      font-size: 24px;
      padding: 0 10px;
    }
    &.affiliate {
      padding-right: 150px;
      span {
        font-weight: bold;
        font-size: 24px;
      }
    }
  }
  .redirection-button {
    margin-right: 10px;
  }
  .navbar-toggler {
    padding: 0 10px 0 0;
    height: 100% !important;
    width: 100% !important;
    display: none;
    button {
      background: #2c2c2c;
    }
  }
  @media (max-width: 1240px) {
    .navbar-toggler {
      display: block;
    }
    .navbar-minimize {
      display: none;
    }
  }
  .partner-list {
    width: 100%;
    display: flex;
    justify-content: center;
    padding-right: 1rem;
    @media (max-width: 1240px) {
      padding-left: 1rem;
    }
    .el-select {
      width: 100%;
      max-width: 600px;
    }
  }
}
</style>
